import { gql } from '@apollo/client';
import { SUBREGION_CARRIER_FIELDS } from '@/graphql/queries/subregion_carrier_mappings';
import { SUBREGION_FIELDS } from '@/graphql/queries/subregions';

export const QUERY_CLIENTS = gql`
    query GetClientsByRole {
        carriers: clients(
            where: { user: { roles: { _has_key: "CARRIER" } }, deactivated: { _eq: false }, approved: { _eq: true } }
            order_by: { business_name: asc_nulls_last }
        ) {
            client_id
            business_name
            supply_chain_legs
            user {
                user_id
                roles
            }
            locations {
                location_id
                location_name
                business_address
                business_unit
                business_city
                business_state
                business_zip
            }
        }
        mm_carriers: clients(
            where: {
                user: { roles: { _has_key: "MIDDLE_MILE_CARRIER" } }
                deactivated: { _eq: false }
                approved: { _eq: true }
            }
            order_by: { business_name: asc_nulls_last }
        ) {
            client_id
            business_name
            supply_chain_legs
            user {
                user_id
                roles
            }
        }
    }
`;

// Tariff and integration badge statuses, each rolled up across the profile's attached shippers.
export const QUERY_CARRIER_STATUSES = gql`
    query GetDeliveryProfileCarrierStatuses($delivery_profile_id: uuid!) {
        results: delivery_profile_carrier_statuses(where: { delivery_profile_id: { _eq: $delivery_profile_id } }) {
            carrier_id
            tariff_status
            integration_status
        }
    }
`;

// The service areas carriers configured for themselves: their own pricing and resource zones, which
// the map overlays while a carrier card is hovered. Some sit on a delivery profile as well, and still
// count. Zip rows select mapping_id because the app cache keys subregion_zip_mappings on it; without it
// the result can't be written to the cache and the query errors.
export const QUERY_CARRIER_REGIONS = gql`
    query GetCarrierRegions($carrier_ids: [uuid!]!) {
        results: subregions(
            where: {
                client_id: { _in: $carrier_ids }
                _or: [{ is_pricing_zone: { _eq: true } }, { is_resource_zone: { _eq: true } }]
            }
        ) {
            subregion_id
            client_id
            zips {
                mapping_id
                subregion_id
                zip
            }
        }
    }
`;

// Everything the profiles grid shows, plus what the profile modal edits so Edit opens without a fetch.
// Deliberately NOT a shared fragment: a fragment's type condition needs __typename, and
// hooks.test.js runs MockedProvider with addTypename={false}, so it would read back empty in tests
// while working in production. Add __typename to every mock before consolidating these.
export const QUERY_DELIVERY_PROFILES = gql`
    query GetDeliveryProfiles($where: delivery_profiles_bool_exp!) {
        results: delivery_profiles(where: $where, order_by: { name: asc_nulls_last }) {
            delivery_profile_id
            name
            organization_id
            assign_zip_type
            commodity
            delivery_types
            special_requirements
            default_order_types
            last_modified_at
            subregions_aggregate {
                aggregate {
                    count
                }
            }
            # The zip types the profile's shippers route on -- see routedZipType.
            auto_assignments {
                auto_assign_id
                zip_type
            }
        }
    }
`;

export const GET_DELIVERY_PROFILE = gql`
    ${SUBREGION_FIELDS}
    ${SUBREGION_CARRIER_FIELDS}
    query GetDeliveryProfile($delivery_profile_id: uuid!) {
        result: delivery_profiles_by_pk(delivery_profile_id: $delivery_profile_id) {
            delivery_profile_id
            name
            organization_id
            assign_zip_type
            commodity
            delivery_types
            special_requirements
            default_order_types
            auto_assignments {
                auto_assign_id
                zip_type
            }
            subregions {
                ...SubregionFields
                carriers(order_by: [{ is_primary: desc_nulls_last }, { created_at: asc }]) {
                    ...SubregionCarrierFields
                }
            }
        }
    }
`;

export const QUERY_DELIVERY_PROFILE_ASSIGNMENTS = gql`
    query GetDeliveryProfileAssignments($where: delivery_profile_assignments_bool_exp!) {
        results: delivery_profile_assignments(where: $where) {
            auto_assign_id
            delivery_profile_id
            shipper_id
            zip_type
            order_type
            shipper {
                client_id
                business_name
            }
        }
    }
`;

export const APPLY_DELIVERY_PROFILE_ASSIGNMENTS = gql`
    mutation ApplyDeliveryProfileAssignments($assignments: [delivery_profile_assignments_insert_input!]!) {
        result: insert_delivery_profile_assignments(
            objects: $assignments
            on_conflict: {
                constraint: unique_delivery_profile_assignments
                update_columns: [delivery_profile_id, zip_type]
            }
        ) {
            returning {
                auto_assign_id
                delivery_profile_id
                shipper_id
                zip_type
                order_type
                shipper {
                    client_id
                    business_name
                }
            }
        }
    }
`;

// autoAssign reads the assignment's zip_type, not the profile's. Without this fan-out a profile
// edit would show the new value while orders kept routing off the old one.
export const SYNC_ASSIGNMENT_ZIP_TYPE = gql`
    mutation SyncAssignmentZipType($delivery_profile_id: uuid!, $zip_type: String!) {
        result: update_delivery_profile_assignments(
            where: { delivery_profile_id: { _eq: $delivery_profile_id } }
            _set: { zip_type: $zip_type }
        ) {
            affected_rows
            returning {
                auto_assign_id
                delivery_profile_id
                shipper_id
                zip_type
                order_type
                shipper {
                    client_id
                    business_name
                }
            }
        }
    }
`;

// `update_columns` is a variable, not a literal list. Hasura compiles it to
// `SET col = excluded.col`, and `excluded.col` for a column ABSENT from the insert object is that
// column's DEFAULT, not its current value -- so a hardcoded list silently wipes whatever the
// caller omitted (commodity -> NULL, the arrays -> '{}', assign_zip_type -> 'DROPOFF').
// upsertProfile derives the list from the fields actually present, so "pass only what I'm
// changing" behaves the way the next caller will expect it to.
export const UPSERT_DELIVERY_PROFILE = gql`
    mutation UpsertDeliveryProfile(
        $delivery_profile: delivery_profiles_insert_input!
        $update_columns: [delivery_profiles_update_column!]!
    ) {
        result: insert_delivery_profiles_one(
            object: $delivery_profile
            on_conflict: { constraint: delivery_profiles_pkey, update_columns: $update_columns }
        ) {
            delivery_profile_id
            name
            organization_id
            assign_zip_type
            commodity
            delivery_types
            special_requirements
            default_order_types
            last_modified_at
            # The profiles grid's list selects these, so a saved profile has to carry them into the cache.
            subregions_aggregate {
                aggregate {
                    count
                }
            }
            auto_assignments {
                auto_assign_id
                zip_type
            }
        }
    }
`;

export const CREATE_SUBREGION = gql`
    ${SUBREGION_FIELDS}
    ${SUBREGION_CARRIER_FIELDS}
    mutation CreateSubregion($subregion: subregions_insert_input!) {
        result: insert_subregions_one(object: $subregion) {
            ...SubregionFields
            carriers(order_by: [{ is_primary: desc_nulls_last }, { created_at: asc }]) {
                ...SubregionCarrierFields
            }
        }
    }
`;

export const UPLOAD_SUBREGIONS = gql`
    ${SUBREGION_FIELDS}
    ${SUBREGION_CARRIER_FIELDS}
    mutation UploadSubregions($subregions: [subregions_insert_input!] = [], $to_delete: [uuid!] = []) {
        deleted: delete_subregions(where: { subregion_id: { _in: $to_delete } }) {
            affected_rows
            returning {
                subregion_id
            }
        }
        created: insert_subregions(objects: $subregions) {
            returning {
                ...SubregionFields
                carriers(order_by: [{ is_primary: desc_nulls_last }, { created_at: asc }]) {
                    ...SubregionCarrierFields
                }
            }
        }
    }
`;
